// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { Val } from './types';

/** The em dash shown wherever Safe Work Australia suppressed a value. */
export const NP = '—';

export function num(v: Val, digits = 0): string {
  if (v === null || !Number.isFinite(v)) return NP;
  return v.toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function money(v: Val, digits = 0): string {
  if (v === null || !Number.isFinite(v)) return NP;
  return `$${v.toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

export function weeks(v: Val, digits = 1): string {
  if (v === null || !Number.isFinite(v)) return NP;
  return `${v.toFixed(digits)} wks`;
}

export function rate(v: Val, digits = 1): string {
  if (v === null || !Number.isFinite(v)) return NP;
  return v.toFixed(digits);
}

export function pct(v: Val, digits = 1): string {
  if (v === null || !Number.isFinite(v)) return NP;
  return `${v.toFixed(digits)}%`;
}

/** A signed change, for year-on-year deltas. */
export function delta(v: Val, digits = 1, suffix = '%'): string {
  if (v === null || !Number.isFinite(v)) return NP;
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(digits)}${suffix}`;
}

export function ratio(a: Val, b: Val, digits = 2): string {
  if (a === null || b === null || !b) return NP;
  return `${(a / b).toFixed(digits)}×`;
}

/** "2023-24" -> "2023–24" (en dash), and calendar years unchanged. */
export function period(p: string): string {
  return p.replace('-', '–');
}

export function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/** Escape for innerHTML. */
export function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Escape for a data-tip written into an HTML STRING. The tooltip renders
 * newlines via `white-space: pre-line`, and a literal "\n" inside an attribute
 * in an innerHTML string is dropped — it has to be the numeric entity. The
 * setAttribute path needs the opposite (a raw newline), which is why these are
 * two separate functions and not one.
 */
export function tipAttr(s: string): string {
  return esc(s).replace(/\n/g, '&#10;');
}

/** Percentage change from a to b. */
export function change(a: Val, b: Val): number | null {
  if (a === null || b === null || a === 0) return null;
  return ((b - a) / a) * 100;
}

/** Last non-null value in a series, with its index. */
export function lastKnown(v: Val[]): { value: number; index: number } | null {
  for (let i = v.length - 1; i >= 0; i--) {
    const x = v[i];
    if (x !== null && Number.isFinite(x)) return { value: x, index: i };
  }
  return null;
}
